import { isCamaraActiva } from '../../utils/camaraStatus';

// Pure helpers for the "Cobranzas" admin views (days-left buckets and MRR renewals).

// Mutually exclusive day buckets; min/max are inclusive and Infinity/-Infinity mean unbounded.
export const DAY_BUCKETS = [
  { key: 'vencidos', label: 'Vencidos', min: -Infinity, max: 0 },
  { key: 'menos30', label: 'Menos de 30 días', min: 1, max: 29 },
  { key: 'menos60', label: 'Menos de 60 días', min: 30, max: 59 },
  { key: 'menos90', label: 'Menos de 90 días', min: 60, max: 89 },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Returns a finite number or null (null/undefined/''/NaN/non-numeric are all "missing").
const toDaysLeft = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const getDaysBucket = (daysLeft) => {
  const days = toDaysLeft(daysLeft);
  if (days === null) return null;
  const bucket = DAY_BUCKETS.find((b) => days >= b.min && days <= b.max);
  return bucket ? bucket.key : null;
};

// Flattens active customers' cameras into rows for one bucket, soonest expiry first.
export const getCamerasByBucket = (customers, bucketKey) => {
  const rows = [];
  (customers || []).forEach((customer) => {
    if (!customer || !customer.active) return;
    (customer.camaras || []).forEach((camara) => {
      if (!isCamaraActiva(camara)) return; // billed = active only (not Terminado/Suspendida)
      const daysLeft = toDaysLeft(camara?.feedDays?.daysLeft);
      if (getDaysBucket(daysLeft) !== bucketKey) return;
      rows.push({
        identifier: camara.identifier,
        customerName: customer.name,
        cameraName: camara.Name,
        daysLeft,
      });
    });
  });
  return rows.sort((a, b) => a.daysLeft - b.daysLeft);
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Days 29-31 fall back to the last day of shorter months.
const dateInMonth = (year, month, day) => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
};

// Next occurrence of renewalDay on or after today (local time); null when renewalDay is invalid.
export const getNextRenewalDate = (renewalDay, today = new Date()) => {
  const day = Number(renewalDay);
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  const base = startOfDay(today);
  const thisMonth = dateInMonth(base.getFullYear(), base.getMonth(), day);
  if (thisMonth >= base) return thisMonth;
  return dateInMonth(base.getFullYear(), base.getMonth() + 1, day);
};

// Whole calendar days from today to date (time of day ignored; DST-safe via rounding).
export const getDaysUntil = (date, today = new Date()) =>
  Math.round((startOfDay(date) - startOfDay(today)) / MS_PER_DAY);

// Joins the webback monthly-billing list with active customers' cameras, soonest renewal first.
export const buildMrrRows = (customers, billingList, today = new Date()) => {
  const billingByIdentifier = new Map();
  (billingList || []).forEach((b) => billingByIdentifier.set(b.identifier, b));

  const rows = [];
  (customers || []).forEach((customer) => {
    if (!customer || !customer.active) return;
    (customer.camaras || []).forEach((camara) => {
      if (!isCamaraActiva(camara)) return;
      const billing = billingByIdentifier.get(camara.identifier);
      if (!billing) return;
      const nextRenewal = getNextRenewalDate(billing.renewalDay, today);
      if (!nextRenewal) return;
      rows.push({
        identifier: camara.identifier,
        customerName: customer.name,
        cameraName: camara.Name,
        renewalDay: billing.renewalDay,
        nextRenewal,
        daysUntil: getDaysUntil(nextRenewal, today),
      });
    });
  });
  return rows.sort((a, b) => a.daysUntil - b.daysUntil);
};

const normalize = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Keeps rows whose customer name contains the query (case and accent insensitive).
export const filterRowsByCustomer = (rows, query) => {
  const q = normalize(query).trim();
  if (!q) return rows;
  return rows.filter((row) => normalize(row.customerName).includes(q));
};
